import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Shield, Search, AlertTriangle, Activity, Eye, Crosshair,
  RefreshCw, ChevronRight, Filter, Zap, Terminal, FileText,
  Plus, Trash2, ToggleLeft, ToggleRight, ArrowRight, Brain,
  Ticket, CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SEV_STYLES = {
  info: 'bg-gray-100 text-gray-600',
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const ALERT_STATUS_STYLES = {
  new: 'bg-red-100 text-red-700',
  investigating: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  false_positive: 'bg-gray-100 text-gray-600',
};

export default function CyberDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Cyber Ops');
  const [view, setView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertTotal, setAlertTotal] = useState(0);
  const [hunts, setHunts] = useState([]);

  // CVE Feed
  const [cves, setCves] = useState([]);
  const [cveSearch, setCveSearch] = useState('');
  const [cveSeverity, setCveSeverity] = useState('');
  const [cveLoading, setCveLoading] = useState(false);
  const [creatingCveRule, setCreatingCveRule] = useState(null);

  // Playbooks
  const [playbooks, setPlaybooks] = useState([]);
  const [activeExecutions, setActiveExecutions] = useState([]);
  const [executingPlaybook, setExecutingPlaybook] = useState(null);
  const [incidentName, setIncidentName] = useState('');

  // Agents
  const [agents, setAgents] = useState([]);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [agentForm, setAgentForm] = useState({ name: '', hostname: '', os: 'windows', ip_address: '' });

  // Heatmap
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapDays, setHeatmapDays] = useState('7');

  // Filters
  const [logSearch, setLogSearch] = useState('');
  const [logSeverity, setLogSeverity] = useState('');
  const [logHost, setLogHost] = useState('');

  // Forms
  const [ruleForm, setRuleForm] = useState({ name: '', description: '', query: '', match_field: 'command_line', severity: 'medium', mitre_technique: '' });
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [huntForm, setHuntForm] = useState({ hypothesis: '', query: '', match_field: 'command_line', mitre_technique: '' });
  const [showHuntForm, setShowHuntForm] = useState(false);
  const [aiRuleDesc, setAiRuleDesc] = useState('');
  const [generatingRule, setGeneratingRule] = useState(false);
  const [analyzingLog, setAnalyzingLog] = useState(null);
  const [logAnalysis, setLogAnalysis] = useState(null);
  const [huntResult, setHuntResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('/api/cyber/logs/stats'),
        api.get('/api/cyber/logs?limit=100'),
        api.get('/api/cyber/rules'),
        api.get('/api/cyber/alerts?limit=50'),
        api.get('/api/cyber/hunts'),
        api.get('/api/cyber/cve/feed?limit=20'),
        api.get('/api/cyber/playbooks'),
        api.get('/api/cyber/agents'),
        api.get(`/api/cyber/heatmap?days=${heatmapDays}`),
      ]);
      const d = (i) => results[i].status === 'fulfilled' ? results[i].value.data : null;
      setStats(d(0));
      setLogs(d(1)?.logs || []);
      setLogTotal(d(1)?.total || 0);
      setRules(d(2)?.rules || []);
      setAlerts(d(3)?.alerts || []);
      setAlertTotal(d(3)?.total || 0);
      setHunts(d(4)?.hunts || []);
      setCves(d(5)?.cves || []);
      setPlaybooks(d(6)?.templates || []);
      setActiveExecutions(d(6)?.active_executions || []);
      setAgents(d(7)?.agents || []);
      setHeatmapData(d(8));
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, [heatmapDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeedDemo = async () => {
    try {
      const { data } = await api.post('/api/cyber/seed-demo');
      toast.success(data.message);
      fetchData();
    } catch { toast.error('Seed failed'); }
  };

  const handleSearchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (logSearch) params.set('search', logSearch);
      if (logSeverity) params.set('severity', logSeverity);
      if (logHost) params.set('hostname', logHost);
      params.set('limit', '100');
      const { data } = await api.get(`/api/cyber/logs?${params}`);
      setLogs(data.logs);
      setLogTotal(data.total);
    } catch { toast.error('Search failed'); }
  };

  const handleCreateRule = async () => {
    if (!ruleForm.name || !ruleForm.query) { toast.error('Name and query required'); return; }
    try {
      const { data } = await api.post('/api/cyber/rules', ruleForm);
      setRules(prev => [data, ...prev]);
      setRuleForm({ name: '', description: '', query: '', match_field: 'command_line', severity: 'medium', mitre_technique: '' });
      setShowRuleForm(false);
      toast.success('Detection rule created');
    } catch { toast.error('Failed to create rule'); }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.put(`/api/cyber/rules/${rule.id}`, { enabled: !rule.enabled });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !rule.enabled } : r));
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
    } catch { toast.error('Update failed'); }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/api/cyber/rules/${ruleId}`);
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Rule deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleUpdateAlertStatus = async (alertId, status) => {
    try {
      await api.put(`/api/cyber/alerts/${alertId}`, { status });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status } : a));
      toast.success('Alert updated');
    } catch { toast.error('Update failed'); }
  };

  const handleCreateTicket = async (alertId) => {
    try {
      const { data } = await api.post(`/api/cyber/alerts/${alertId}/create-ticket`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, ticket_id: data.ticket.id, status: 'investigating' } : a));
      toast.success('Security ticket created');
    } catch { toast.error('Ticket creation failed'); }
  };

  const handleRunHunt = async () => {
    if (!huntForm.hypothesis || !huntForm.query) { toast.error('Hypothesis and query required'); return; }
    try {
      const { data } = await api.post('/api/cyber/hunts', huntForm);
      setHunts(prev => [data, ...prev]);
      setHuntResult(data);
      toast.success(`Hunt complete: ${data.match_count} matches found`);
    } catch { toast.error('Hunt failed'); }
  };

  const handleAiAnalyze = async (log) => {
    setAnalyzingLog(log.id);
    setLogAnalysis(null);
    try {
      const { data } = await api.post('/api/cyber/ai/analyze-log', { log });
      setLogAnalysis(data);
    } catch { toast.error('AI analysis failed'); }
    finally { setAnalyzingLog(null); }
  };

  const handleAiGenerateRule = async () => {
    if (!aiRuleDesc) { toast.error('Describe the threat to detect'); return; }
    setGeneratingRule(true);
    try {
      const { data } = await api.post('/api/cyber/ai/generate-rule', { description: aiRuleDesc });
      if (data.name) {
        setRuleForm({
          name: data.name,
          description: data.description || '',
          query: data.query || '',
          match_field: data.match_field || 'command_line',
          severity: data.severity || 'medium',
          mitre_technique: data.mitre_technique || '',
        });
        setShowRuleForm(true);
        toast.success('Rule generated — review and save');
      }
    } catch { toast.error('AI generation failed'); }
    finally { setGeneratingRule(false); }
  };

  const handleSearchCves = async () => {
    setCveLoading(true);
    try {
      const params = new URLSearchParams();
      if (cveSearch) params.set('keyword', cveSearch);
      if (cveSeverity) params.set('severity', cveSeverity);
      params.set('limit', '20');
      const { data } = await api.get(`/api/cyber/cve/feed?${params}`);
      setCves(data.cves || []);
    } catch { toast.error('CVE search failed'); }
    finally { setCveLoading(false); }
  };

  const handleCreateRuleFromCve = async (cve) => {
    setCreatingCveRule(cve.id);
    try {
      const { data } = await api.post(`/api/cyber/cve/${cve.id}/create-rule`, { description: cve.description });
      setRules(prev => [data, ...prev]);
      toast.success(`Detection rule created for ${cve.id}`);
    } catch { toast.error('Rule creation failed'); }
    finally { setCreatingCveRule(null); }
  };

  const handleExecutePlaybook = async (playbookId) => {
    if (!incidentName.trim()) { toast.error('Enter incident name'); return; }
    setExecutingPlaybook(playbookId);
    try {
      const { data } = await api.post(`/api/cyber/playbooks/${playbookId}/execute`, { incident_name: incidentName });
      setActiveExecutions(prev => [data, ...prev]);
      setIncidentName('');
      toast.success(`Playbook started: ${data.total_steps} tasks created`);
    } catch { toast.error('Playbook execution failed'); }
    finally { setExecutingPlaybook(null); }
  };

  const handleCompleteStep = async (execId, stepOrder) => {
    try {
      const { data } = await api.put(`/api/cyber/playbooks/executions/${execId}/steps/${stepOrder}`, { status: 'completed' });
      setActiveExecutions(prev => prev.map(e => e.id === execId ? data : e));
      toast.success('Step completed');
    } catch { toast.error('Update failed'); }
  };

  const handleRegisterAgent = async () => {
    if (!agentForm.name) { toast.error('Agent name required'); return; }
    try {
      const { data } = await api.post('/api/cyber/agents/register', agentForm);
      setAgents(prev => [data, ...prev]);
      setAgentForm({ name: '', hostname: '', os: 'windows', ip_address: '' });
      setShowAgentForm(false);
      toast.success(`Agent registered: ${data.id}`);
    } catch { toast.error('Registration failed'); }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await api.delete(`/api/cyber/agents/${agentId}`);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      toast.success('Agent removed');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="cyber-dashboard">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Cyber Operations</h1>
              <p className="text-sm text-gray-500 mt-1">Threat hunting, detection rules, and security alerting</p>
            </div>
            {stats?.total_logs === 0 && (
              <Button onClick={handleSeedDemo} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="seed-demo-btn">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> Seed Demo Data
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-0">
            {['overview', 'logs', 'rules', 'alerts', 'hunts', 'heatmap', 'playbooks', 'agents', 'cve'].map(tab => (
              <button key={tab} onClick={() => setView(tab)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize whitespace-nowrap ${view === tab ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`cyber-tab-${tab}`}
              >{tab === 'overview' ? 'Overview' : tab === 'logs' ? 'Log Explorer' : tab === 'rules' ? 'Detection Rules' : tab === 'hunts' ? 'Threat Hunts' : tab === 'cve' ? 'CVE Feed' : tab === 'playbooks' ? 'IR Playbooks' : tab === 'agents' ? 'Agents' : tab === 'heatmap' ? 'Threat Heatmap' : tab}</button>
            ))}
          </div>

          {/* ── OVERVIEW ──────────────────────────────────── */}
          {view === 'overview' && stats && (
            <div className="space-y-6" data-testid="cyber-overview">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-[#D4A843]" /><p className="text-xs text-gray-500 uppercase tracking-wider">Total Logs</p></div>
                  <p className="font-heading text-2xl font-bold text-gray-900">{stats.total_logs}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-600" /><p className="text-xs text-red-600 font-semibold uppercase tracking-wider">Open Alerts</p></div>
                  <p className="font-heading text-2xl font-bold text-red-700">{stats.open_alerts}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-amber-600" /><p className="text-xs text-amber-600 font-semibold uppercase tracking-wider">Detection Rules</p></div>
                  <p className="font-heading text-2xl font-bold text-amber-700">{rules.length}</p>
                </div>
                <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2"><Crosshair className="w-4 h-4 text-[#D4A843]" /><p className="text-xs text-[#D4A843] font-semibold uppercase tracking-wider">Threat Hunts</p></div>
                  <p className="font-heading text-2xl font-bold text-[#D4A843]">{hunts.length}</p>
                </div>
              </div>

              {/* Severity Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Log Severity</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.severity_counts || {}).map(([sev, count]) => (
                      <div key={sev} className="flex items-center gap-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded min-w-[60px] text-center ${SEV_STYLES[sev]}`}>{sev}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${sev === 'critical' ? 'bg-red-500' : sev === 'high' ? 'bg-orange-500' : sev === 'medium' ? 'bg-amber-400' : sev === 'low' ? 'bg-blue-400' : 'bg-gray-300'}`}
                            style={{ width: `${stats.total_logs ? (count / stats.total_logs * 100) : 0}%` }} />
                        </div>
                        <span className="text-sm font-bold text-gray-700 min-w-[30px] text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Top Hosts</h3>
                  <div className="space-y-2">
                    {(stats.top_hosts || []).slice(0, 6).map(h => (
                      <div key={h.hostname} className="flex items-center justify-between p-2 rounded bg-gray-50">
                        <span className="text-sm font-medium text-gray-900 font-mono">{h.hostname}</span>
                        <span className="text-xs font-bold text-gray-500">{h.count} events</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Alerts */}
              {alerts.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" /> Recent Alerts
                  </h3>
                  <div className="space-y-2">
                    {alerts.slice(0, 5).map(a => (
                      <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`overview-alert-${a.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[a.severity]}`}>{a.severity}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{a.rule_name}</p>
                            <p className="text-xs text-gray-400 font-mono truncate">{a.hostname} | {a.username}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${ALERT_STATUS_STYLES[a.status]}`}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3 text-xs rounded-lg" onClick={() => setView('alerts')} data-testid="view-all-alerts-btn">
                    View All Alerts <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}

              {stats.total_logs === 0 && (
                <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                  <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-700 mb-2">No Security Data Yet</h3>
                  <p className="text-sm text-gray-400 mb-4">Seed demo data to explore the threat hunting module</p>
                  <Button onClick={handleSeedDemo} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="seed-demo-empty-btn">
                    <Zap className="w-3.5 h-3.5 mr-1.5" /> Seed Demo Data
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── LOG EXPLORER ──────────────────────────────── */}
          {view === 'logs' && (
            <div className="space-y-4" data-testid="log-explorer">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Search</Label>
                    <Input value={logSearch} onChange={e => setLogSearch(e.target.value)} placeholder="Search commands, processes, users..." className="mt-1 rounded-lg" data-testid="log-search-input"
                      onKeyDown={e => e.key === 'Enter' && handleSearchLogs()} />
                  </div>
                  <div>
                    <Label className="text-xs">Severity</Label>
                    <Select value={logSeverity} onValueChange={v => setLogSeverity(v === 'all' ? '' : v)}>
                      <SelectTrigger className="mt-1 rounded-lg w-28" data-testid="log-severity-filter"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Host</Label>
                    <Input value={logHost} onChange={e => setLogHost(e.target.value)} placeholder="Hostname" className="mt-1 rounded-lg w-32" data-testid="log-host-filter" />
                  </div>
                  <Button onClick={handleSearchLogs} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="log-search-btn">
                    <Search className="w-3.5 h-3.5 mr-1" /> Search
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">{logTotal} logs found</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="log-table">
                    <thead><tr className="border-b bg-gray-50">
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Time</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Severity</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Host</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">User</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Process</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Command</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {logs.map(log => (
                        <tr key={log.id} className={`hover:bg-gray-50 ${log.severity === 'critical' ? 'bg-red-50/50' : log.severity === 'high' ? 'bg-orange-50/30' : ''}`} data-testid={`log-row-${log.id}`}>
                          <td className="px-3 py-2 text-gray-400 font-mono whitespace-nowrap">{new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[log.severity]}`}>{log.severity}</span></td>
                          <td className="px-3 py-2 font-mono text-gray-700">{log.hostname}</td>
                          <td className="px-3 py-2 text-gray-600">{log.username}</td>
                          <td className="px-3 py-2 font-mono text-gray-700">{log.process_name}</td>
                          <td className="px-3 py-2 font-mono text-gray-600 max-w-[300px] truncate" title={log.command_line}>{log.command_line}</td>
                          <td className="px-3 py-2">
                            {(log.severity === 'high' || log.severity === 'critical') && (
                              <Button size="sm" variant="outline" className="text-[10px] rounded h-6 px-2"
                                onClick={() => handleAiAnalyze(log)}
                                disabled={analyzingLog === log.id}
                                data-testid={`analyze-log-${log.id}`}
                              >
                                <Brain className="w-3 h-3 mr-0.5" />{analyzingLog === log.id ? '...' : 'AI'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {logs.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No logs found</p>}
              </div>

              {/* AI Analysis Result */}
              {logAnalysis && !logAnalysis.error && (
                <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-white animate-fade-in" data-testid="ai-analysis-result">
                  <div className="flex items-center gap-2 mb-4">
                    <Brain className="w-5 h-5 text-[#D4A843]" />
                    <h3 className="text-sm font-bold uppercase tracking-wider">AI Threat Analysis</h3>
                    <span className="text-[10px] text-gray-400 ml-auto">GPT-5.2</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase">Threat Level</p>
                      <p className={`text-sm font-bold ${logAnalysis.threat_level === 'critical' ? 'text-red-400' : logAnalysis.threat_level === 'high' ? 'text-orange-400' : 'text-emerald-400'}`}>{logAnalysis.threat_level}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase">Malicious</p>
                      <p className={`text-sm font-bold ${logAnalysis.is_malicious ? 'text-red-400' : 'text-emerald-400'}`}>{logAnalysis.is_malicious ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase">MITRE</p>
                      <p className="text-sm font-bold text-[#D4A843]">{logAnalysis.mitre_technique || 'N/A'}</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase">False Positive</p>
                      <p className="text-sm font-bold text-gray-300">{logAnalysis.false_positive_likelihood || 'N/A'}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-300 mb-3">{logAnalysis.explanation}</p>
                  {logAnalysis.recommended_actions?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Recommended Actions</p>
                      <ul className="space-y-1">{logAnalysis.recommended_actions.map((a, i) => (
                        <li key={`item-${i}`} className="text-xs text-gray-300 flex items-start gap-1.5"><ChevronRight className="w-3 h-3 text-[#D4A843] shrink-0 mt-0.5" />{a}</li>
                      ))}</ul>
                    </div>
                  )}
                  <Button size="sm" variant="outline" className="mt-3 text-xs text-white border-white/20 hover:bg-white/10" onClick={() => setLogAnalysis(null)}>Dismiss</Button>
                </div>
              )}
            </div>
          )}

          {/* ── DETECTION RULES ───────────────────────────── */}
          {view === 'rules' && (
            <div className="space-y-4" data-testid="detection-rules">
              {/* AI Rule Generator */}
              <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-white" data-testid="ai-rule-generator">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-[#D4A843]" />
                  <h3 className="text-sm font-bold uppercase tracking-wider">AI Rule Generator</h3>
                  <span className="text-[10px] text-gray-400 ml-1">GPT-5.2</span>
                </div>
                <div className="flex gap-2">
                  <Input value={aiRuleDesc} onChange={e => setAiRuleDesc(e.target.value)} placeholder="Describe the threat... e.g., 'Detect lateral movement via RDP'" className="rounded-lg bg-white/10 border-white/20 text-white placeholder:text-gray-400" data-testid="ai-rule-input" />
                  <Button onClick={handleAiGenerateRule} disabled={generatingRule} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg shrink-0" data-testid="ai-generate-rule-btn">
                    <Zap className="w-3.5 h-3.5 mr-1" />{generatingRule ? 'Generating...' : 'Generate'}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{rules.length} Rules</h3>
                <Button onClick={() => setShowRuleForm(!showRuleForm)} variant="outline" className="text-xs rounded-lg" data-testid="toggle-rule-form-btn">
                  <Plus className="w-3 h-3 mr-1" /> {showRuleForm ? 'Cancel' : 'New Rule'}
                </Button>
              </div>

              {showRuleForm && (
                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3 animate-fade-in" data-testid="rule-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Rule Name *</Label><Input value={ruleForm.name} onChange={e => setRuleForm({...ruleForm, name: e.target.value})} className="mt-1 rounded-lg" data-testid="rule-name-input" /></div>
                    <div><Label className="text-xs">MITRE Technique</Label><Input value={ruleForm.mitre_technique} onChange={e => setRuleForm({...ruleForm, mitre_technique: e.target.value})} className="mt-1 rounded-lg" placeholder="T1059.001" data-testid="rule-mitre-input" /></div>
                  </div>
                  <div><Label className="text-xs">Description</Label><Textarea value={ruleForm.description} onChange={e => setRuleForm({...ruleForm, description: e.target.value})} className="mt-1 rounded-lg" rows={2} data-testid="rule-desc-input" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Query Pattern (regex) *</Label><Input value={ruleForm.query} onChange={e => setRuleForm({...ruleForm, query: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" placeholder="EncodedCommand|mimikatz" data-testid="rule-query-input" /></div>
                    <div><Label className="text-xs">Match Field</Label>
                      <Select value={ruleForm.match_field} onValueChange={v => setRuleForm({...ruleForm, match_field: v})}>
                        <SelectTrigger className="mt-1 rounded-lg" data-testid="rule-field-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="command_line">Command Line</SelectItem>
                          <SelectItem value="process_name">Process Name</SelectItem>
                          <SelectItem value="username">Username</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Severity</Label>
                      <Select value={ruleForm.severity} onValueChange={v => setRuleForm({...ruleForm, severity: v})}>
                        <SelectTrigger className="mt-1 rounded-lg" data-testid="rule-severity-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleCreateRule} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="save-rule-btn">Save Rule</Button>
                </div>
              )}

              <div className="space-y-2">
                {rules.map(rule => (
                  <div key={rule.id} className={`bg-white border rounded-lg p-4 ${rule.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'}`} data-testid={`rule-${rule.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <button onClick={() => handleToggleRule(rule)} className="shrink-0" data-testid={`rule-toggle-${rule.id}`}>
                          {rule.enabled ? <ToggleRight className="w-6 h-6 text-[#D4A843]" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}
                        </button>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900">{rule.name}</p>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[rule.severity]}`}>{rule.severity}</span>
                            {rule.mitre_technique && <span className="text-[10px] font-mono bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded">{rule.mitre_technique}</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                          <p className="text-[10px] text-gray-400 font-mono mt-0.5">Pattern: {rule.query} | Field: {rule.match_field}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 shrink-0" onClick={() => handleDeleteRule(rule.id)} data-testid={`rule-delete-${rule.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {rules.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No detection rules. Create one above or use AI to generate.</p>}
            </div>
          )}

          {/* ── ALERTS ────────────────────────────────────── */}
          {view === 'alerts' && (
            <div className="space-y-4" data-testid="alerts-view">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{alertTotal} Alerts</h3>
              </div>
              <div className="space-y-2">
                {alerts.map(alert => (
                  <div key={alert.id} className={`bg-white border rounded-lg p-4 ${alert.status === 'new' ? 'border-red-200' : 'border-gray-200'}`} data-testid={`alert-${alert.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[alert.severity]}`}>{alert.severity}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${ALERT_STATUS_STYLES[alert.status]}`}>{alert.status.replace('_', ' ')}</span>
                          {alert.mitre_technique && <span className="text-[10px] font-mono bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded">{alert.mitre_technique}</span>}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-1.5">{alert.rule_name}</p>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          <p><span className="text-gray-400">Host:</span> {alert.hostname} | <span className="text-gray-400">User:</span> {alert.username}</p>
                          <p className="font-mono text-gray-400 truncate" title={alert.command_line}>{alert.command_line}</p>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">{new Date(alert.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {alert.status === 'new' && (
                          <>
                            <Button size="sm" className="text-[10px] rounded bg-amber-500 hover:bg-amber-600 text-white h-7"
                              onClick={() => handleUpdateAlertStatus(alert.id, 'investigating')} data-testid={`alert-investigate-${alert.id}`}>
                              <Eye className="w-3 h-3 mr-0.5" /> Investigate
                            </Button>
                            <Button size="sm" variant="outline" className="text-[10px] rounded h-7"
                              onClick={() => handleUpdateAlertStatus(alert.id, 'false_positive')} data-testid={`alert-fp-${alert.id}`}>
                              False Positive
                            </Button>
                          </>
                        )}
                        {alert.status === 'investigating' && (
                          <Button size="sm" className="text-[10px] rounded bg-emerald-600 text-white h-7"
                            onClick={() => handleUpdateAlertStatus(alert.id, 'resolved')} data-testid={`alert-resolve-${alert.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-0.5" /> Resolve
                          </Button>
                        )}
                        {!alert.ticket_id && alert.status !== 'false_positive' && (
                          <Button size="sm" variant="outline" className="text-[10px] rounded h-7 text-[#D4A843] border-[#D4A843]/30"
                            onClick={() => handleCreateTicket(alert.id)} data-testid={`alert-ticket-${alert.id}`}>
                            <Ticket className="w-3 h-3 mr-0.5" /> Create Ticket
                          </Button>
                        )}
                        {alert.ticket_id && (
                          <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ticket created</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {alerts.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No alerts. Detection rules will trigger alerts when matching logs are found.</p>}
            </div>
          )}

          {/* ── THREAT HUNTS ──────────────────────────────── */}
          {view === 'hunts' && (
            <div className="space-y-4" data-testid="hunts-view">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Threat Hunts</h3>
                <Button onClick={() => { setShowHuntForm(!showHuntForm); setHuntResult(null); }} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="new-hunt-btn">
                  <Crosshair className="w-3.5 h-3.5 mr-1" /> {showHuntForm ? 'Cancel' : 'New Hunt'}
                </Button>
              </div>

              {showHuntForm && (
                <div className="bg-white border border-[#D4A843]/20 rounded-lg p-5 space-y-3 animate-fade-in" data-testid="hunt-form">
                  <div>
                    <Label className="text-xs font-semibold">Hypothesis *</Label>
                    <Textarea value={huntForm.hypothesis} onChange={e => setHuntForm({...huntForm, hypothesis: e.target.value})} className="mt-1 rounded-lg" rows={2} placeholder="e.g., An attacker may be using encoded PowerShell commands to evade detection" data-testid="hunt-hypothesis-input" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Query (regex) *</Label>
                      <Input value={huntForm.query} onChange={e => setHuntForm({...huntForm, query: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" placeholder="EncodedCommand|-enc" data-testid="hunt-query-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Search Field</Label>
                      <Select value={huntForm.match_field} onValueChange={v => setHuntForm({...huntForm, match_field: v})}>
                        <SelectTrigger className="mt-1 rounded-lg" data-testid="hunt-field-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="command_line">Command Line</SelectItem>
                          <SelectItem value="process_name">Process Name</SelectItem>
                          <SelectItem value="username">Username</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">MITRE Technique</Label>
                      <Input value={huntForm.mitre_technique} onChange={e => setHuntForm({...huntForm, mitre_technique: e.target.value})} className="mt-1 rounded-lg" placeholder="T1059.001" data-testid="hunt-mitre-input" />
                    </div>
                  </div>
                  <Button onClick={handleRunHunt} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="run-hunt-btn">
                    <Crosshair className="w-3.5 h-3.5 mr-1" /> Run Hunt
                  </Button>
                </div>
              )}

              {/* Hunt Results */}
              {huntResult && (
                <div className="bg-white border border-[#D4A843]/20 rounded-lg p-5 animate-fade-in" data-testid="hunt-results">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">Hunt Results</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{huntResult.hypothesis}</p>
                    </div>
                    <span className={`text-sm font-bold ${huntResult.match_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {huntResult.match_count} matches
                    </span>
                  </div>
                  {huntResult.findings?.length > 0 && (
                    <div className="max-h-[300px] overflow-y-auto border border-gray-100 rounded-lg">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b bg-gray-50 sticky top-0">
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Time</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Host</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">User</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Command</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {huntResult.findings.slice(0, 50).map(f => (
                            <tr key={f.id} className="hover:bg-red-50/30">
                              <td className="px-3 py-1.5 text-gray-400 font-mono whitespace-nowrap">{new Date(f.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                              <td className="px-3 py-1.5 font-mono text-gray-700">{f.hostname}</td>
                              <td className="px-3 py-1.5 text-gray-600">{f.username}</td>
                              <td className="px-3 py-1.5 font-mono text-gray-600 truncate max-w-[300px]" title={f.command_line}>{f.command_line}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Past Hunts */}
              {hunts.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Hunt History</h4>
                  <div className="space-y-2">
                    {hunts.map(h => (
                      <div key={h.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`hunt-${h.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{h.hypothesis}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">Query: {h.query} | Field: {h.match_field}</p>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <span className={`text-sm font-bold ${h.match_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{h.match_count} hits</span>
                            <p className="text-[10px] text-gray-400">{new Date(h.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ── THREAT HEATMAP ────────────────────────────── */}
          {view === 'heatmap' && heatmapData && (
            <div className="space-y-6" data-testid="heatmap-view">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Threat Activity Heatmap</h3>
                <div className="flex items-center gap-2">
                  <Select value={heatmapDays} onValueChange={v => setHeatmapDays(v)}>
                    <SelectTrigger className="w-28 rounded-lg" data-testid="heatmap-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-gray-400">{heatmapData.total_events} events</span>
                </div>
              </div>

              {/* Time Grid Heatmap */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Activity by Day & Hour</h4>
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    <div className="flex gap-0.5 mb-1 ml-10">
                      {Array.from({ length: 24 }, (_, i) => (
                        <div key={`item-${i}`} className="flex-1 text-center text-[8px] text-gray-400">{i}</div>
                      ))}
                    </div>
                    {(heatmapData.time_grid || []).map((row, dayIdx) => {
                      const maxVal = Math.max(...(heatmapData.time_grid || []).flat(), 1);
                      return (
                        <div key={`hm-row-${heatmapData.day_labels?.[dayIdx] || dayIdx}`} className="flex items-center gap-0.5 mb-0.5">
                          <span className="text-[9px] text-gray-500 w-9 text-right pr-1">{heatmapData.day_labels?.[dayIdx] || ''}</span>
                          {row.map((val, hourIdx) => {
                            const intensity = val / maxVal;
                            const bg = val === 0 ? 'bg-gray-100' : intensity > 0.75 ? 'bg-red-600' : intensity > 0.5 ? 'bg-red-400' : intensity > 0.25 ? 'bg-orange-300' : 'bg-amber-200';
                            return (
                              <div key={`hm-cell-${dayIdx}-${hourIdx}`} className={`flex-1 h-5 rounded-sm ${bg} transition-colors`} title={`${heatmapData.day_labels?.[dayIdx]} ${hourIdx}:00 — ${val} events`} data-testid={`hm-cell-${dayIdx}-${hourIdx}`} />
                            );
                          })}
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-2 mt-3 ml-10">
                      <span className="text-[9px] text-gray-400">Less</span>
                      <div className="flex gap-0.5">
                        {['bg-gray-100', 'bg-amber-200', 'bg-orange-300', 'bg-red-400', 'bg-red-600'].map((c, i) => (
                          <div key={`item-${i}`} className={`w-4 h-3 rounded-sm ${c}`} />
                        ))}
                      </div>
                      <span className="text-[9px] text-gray-400">More</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Host Heatmap */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Top Targeted Hosts</h4>
                  <div className="space-y-2">
                    {(heatmapData.host_heatmap || []).map((h, i) => {
                      const maxH = (heatmapData.host_heatmap || [])[0]?.count || 1;
                      return (
                        <div key={`item-${i}`} className="flex items-center gap-3" data-testid={`hm-host-${i}`}>
                          <span className="text-xs font-mono text-gray-700 w-24 truncate">{h.hostname}</span>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all" style={{ width: `${(h.count / maxH) * 100}%` }} />
                          </div>
                          <span className="text-xs font-bold text-gray-600 w-8 text-right">{h.count}</span>
                        </div>
                      );
                    })}
                    {(heatmapData.host_heatmap || []).length === 0 && <p className="text-xs text-gray-400 text-center py-4">No host data</p>}
                  </div>
                </div>

                {/* MITRE Technique Heatmap */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">MITRE ATT&CK Techniques</h4>
                  <div className="space-y-2">
                    {(heatmapData.technique_heatmap || []).map((t, i) => {
                      const maxT = (heatmapData.technique_heatmap || [])[0]?.count || 1;
                      return (
                        <div key={`item-${i}`} className="flex items-center gap-3" data-testid={`hm-tech-${i}`}>
                          <div className="w-32 min-w-0">
                            <span className="text-[10px] font-mono text-[#D4A843]">{t.technique}</span>
                            <p className="text-[9px] text-gray-400 truncate">{t.name}</p>
                          </div>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#D4A843] to-purple-600 rounded-full transition-all" style={{ width: `${(t.count / maxT) * 100}%` }} />
                          </div>
                          <span className="text-xs font-bold text-gray-600 w-8 text-right">{t.count}</span>
                        </div>
                      );
                    })}
                    {(heatmapData.technique_heatmap || []).length === 0 && <p className="text-xs text-gray-400 text-center py-4">No technique data</p>}
                  </div>
                </div>
              </div>

              {/* Daily Severity Trend */}
              {(heatmapData.daily_trend || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Daily Severity Trend</h4>
                  <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                    {heatmapData.daily_trend.map(d => {
                      const total = d.critical + d.high + d.medium + d.low;
                      const maxD = Math.max(...heatmapData.daily_trend.map(x => x.critical + x.high + x.medium + x.low), 1);
                      return (
                        <div key={d.date} className="flex items-center gap-3" data-testid={`hm-day-${d.date}`}>
                          <span className="text-[10px] font-mono text-gray-500 w-20">{d.date}</span>
                          <div className="flex-1 h-4 bg-gray-50 rounded-full overflow-hidden flex">
                            {d.critical > 0 && <div className="h-full bg-red-600" style={{ width: `${(d.critical / maxD) * 100}%` }} title={`${d.critical} critical`} />}
                            {d.high > 0 && <div className="h-full bg-orange-400" style={{ width: `${(d.high / maxD) * 100}%` }} title={`${d.high} high`} />}
                            {d.medium > 0 && <div className="h-full bg-amber-300" style={{ width: `${(d.medium / maxD) * 100}%` }} title={`${d.medium} medium`} />}
                            {d.low > 0 && <div className="h-full bg-blue-300" style={{ width: `${(d.low / maxD) * 100}%` }} title={`${d.low} low`} />}
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 w-6 text-right">{total}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    {[{c: 'bg-red-600', l: 'Critical'}, {c: 'bg-orange-400', l: 'High'}, {c: 'bg-amber-300', l: 'Medium'}, {c: 'bg-blue-300', l: 'Low'}].map(x => (
                      <div key={x.l} className="flex items-center gap-1"><div className={`w-2.5 h-2.5 rounded ${x.c}`} /><span className="text-[9px] text-gray-400">{x.l}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Process Heatmap */}
              {(heatmapData.process_heatmap || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">Suspicious Processes</h4>
                  <div className="flex flex-wrap gap-2">
                    {heatmapData.process_heatmap.map((p, i) => {
                      const maxP = heatmapData.process_heatmap[0]?.count || 1;
                      const intensity = p.count / maxP;
                      const bg = intensity > 0.75 ? 'bg-red-100 text-red-700 border-red-200' : intensity > 0.5 ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-gray-100 text-gray-700 border-gray-200';
                      return (
                        <span key={`item-${i}`} className={`text-xs font-mono px-3 py-1.5 rounded-lg border ${bg}`} data-testid={`hm-proc-${i}`}>
                          {p.process} <span className="font-bold ml-1">{p.count}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {heatmapData.total_events === 0 && (
                <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-400">No threat activity data for this period. Seed demo data or ingest logs from agents.</p>
                </div>
              )}
            </div>
          )}

          {/* ── IR PLAYBOOKS ──────────────────────────────── */}
          {view === 'playbooks' && (
            <div className="space-y-6" data-testid="playbooks-view">
              {/* Active Executions */}
              {activeExecutions.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Active Incidents</h3>
                  <div className="space-y-3">
                    {activeExecutions.map(exec => (
                      <div key={exec.id} className={`bg-white border rounded-lg p-5 ${exec.severity === 'critical' ? 'border-red-200' : 'border-amber-200'}`} data-testid={`exec-${exec.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[exec.severity]}`}>{exec.severity}</span>
                              <h4 className="text-sm font-bold text-gray-900">{exec.incident_name}</h4>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{exec.playbook_name} | Started by {exec.started_by_name} | {new Date(exec.started_at).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-[#D4A843]">{exec.completed_steps}/{exec.total_steps}</p>
                            <p className="text-[10px] text-gray-400">steps done</p>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="h-2 bg-gray-100 rounded-full mb-3">
                          <div className="h-full bg-[#D4A843] rounded-full transition-all" style={{ width: `${(exec.completed_steps / exec.total_steps) * 100}%` }} />
                        </div>
                        {/* Steps */}
                        <div className="space-y-1.5">
                          {(exec.steps || []).map(step => (
                            <div key={step.order} className={`flex items-center gap-3 p-2 rounded ${step.status === 'completed' ? 'bg-emerald-50' : 'bg-gray-50'}`} data-testid={`step-${exec.id}-${step.order}`}>
                              <button
                                onClick={() => step.status !== 'completed' && handleCompleteStep(exec.id, step.order)}
                                disabled={step.status === 'completed'}
                                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${step.status === 'completed' ? 'bg-emerald-500 text-white' : 'border-2 border-gray-300 hover:border-[#D4A843]'}`}
                                data-testid={`complete-step-${exec.id}-${step.order}`}
                              >
                                {step.status === 'completed' && <CheckCircle2 className="w-4 h-4" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium ${step.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                  <span className="text-gray-400 mr-1">{step.order}.</span> {step.title}
                                </p>
                              </div>
                              <span className="text-[10px] text-gray-400 shrink-0">{step.sla_minutes}m SLA</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Playbook Templates */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Playbook Templates</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {playbooks.map(pb => (
                    <div key={pb.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`playbook-${pb.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[pb.severity]}`}>{pb.severity}</span>
                        <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{pb.category}</span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900">{pb.name}</h4>
                      <p className="text-xs text-gray-500 mt-1">{pb.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
                        <span>{pb.steps.length} steps</span>
                        <span>~{pb.estimated_hours}h estimated</span>
                      </div>
                      {/* Steps preview */}
                      <div className="mt-3 space-y-1 max-h-[120px] overflow-y-auto">
                        {pb.steps.map(s => (
                          <div key={s.order} className="flex items-center gap-2 text-[10px] text-gray-600">
                            <span className="text-gray-300 font-mono w-4">{s.order}.</span>
                            <span className="truncate">{s.title}</span>
                          </div>
                        ))}
                      </div>
                      {/* Execute */}
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                        <Input value={executingPlaybook === pb.id ? incidentName : ''} onChange={e => { setExecutingPlaybook(pb.id); setIncidentName(e.target.value); }} placeholder="Incident name..." className="rounded-lg text-xs h-8 flex-1" data-testid={`pb-incident-${pb.id}`} />
                        <Button size="sm" onClick={() => handleExecutePlaybook(pb.id)} disabled={executingPlaybook === pb.id && !incidentName.trim()} className="bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs h-8 shrink-0" data-testid={`pb-execute-${pb.id}`}>
                          <Zap className="w-3 h-3 mr-1" /> Execute
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


          {/* ── AGENTS ────────────────────────────────────── */}
          {view === 'agents' && (
            <div className="space-y-4" data-testid="agents-view">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{agents.length} Registered Agents</h3>
                <Button onClick={() => setShowAgentForm(!showAgentForm)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="register-agent-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> {showAgentForm ? 'Cancel' : 'Register Agent'}
                </Button>
              </div>

              {showAgentForm && (
                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3 animate-fade-in" data-testid="agent-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Agent Name *</Label><Input value={agentForm.name} onChange={e => setAgentForm({...agentForm, name: e.target.value})} className="mt-1 rounded-lg" placeholder="e.g., SysmonAgent-WKS001" data-testid="agent-name-input" /></div>
                    <div><Label className="text-xs">Hostname</Label><Input value={agentForm.hostname} onChange={e => setAgentForm({...agentForm, hostname: e.target.value})} className="mt-1 rounded-lg" placeholder="WKS-001" data-testid="agent-host-input" /></div>
                    <div><Label className="text-xs">OS</Label>
                      <Select value={agentForm.os} onValueChange={v => setAgentForm({...agentForm, os: v})}>
                        <SelectTrigger className="mt-1 rounded-lg" data-testid="agent-os-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="windows">Windows</SelectItem>
                          <SelectItem value="linux">Linux</SelectItem>
                          <SelectItem value="macos">macOS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">IP Address</Label><Input value={agentForm.ip_address} onChange={e => setAgentForm({...agentForm, ip_address: e.target.value})} className="mt-1 rounded-lg" placeholder="10.0.0.50" data-testid="agent-ip-input" /></div>
                  </div>
                  <Button onClick={handleRegisterAgent} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="agent-register-btn">Register Agent</Button>
                </div>
              )}

              <div className="space-y-3">
                {agents.map(agent => {
                  const isActive = agent.status === 'active';
                  const lastHb = agent.last_heartbeat ? new Date(agent.last_heartbeat) : null;
                  const hbAgo = lastHb ? Math.round((Date.now() - lastHb.getTime()) / 60000) : null;
                  return (
                    <div key={agent.id} className={`bg-white border rounded-lg p-5 ${isActive ? 'border-emerald-200' : 'border-gray-200 opacity-70'}`} data-testid={`agent-${agent.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900">{agent.name}</p>
                              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{agent.status}</span>
                              <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{agent.os}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                              <span>Host: {agent.hostname || '—'}</span>
                              <span>IP: {agent.ip_address || '—'}</span>
                              <span>Logs sent: {agent.total_logs_sent || 0}</span>
                              {hbAgo !== null && <span>Last heartbeat: {hbAgo < 1 ? 'just now' : `${hbAgo}m ago`}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { navigator.clipboard.writeText(agent.id); toast.success('Agent ID copied'); }} className="text-[10px] text-[#D4A843] hover:underline cursor-pointer" data-testid={`agent-copy-${agent.id}`}>
                            Copy ID
                          </button>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 h-7" onClick={() => handleDeleteAgent(agent.id)} data-testid={`agent-del-${agent.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      {/* API Config */}
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Ingestion Endpoint</p>
                        <code className="text-[10px] text-gray-700 font-mono break-all">POST {process.env.REACT_APP_BACKEND_URL}/api/cyber/agents/{agent.id}/ingest</code>
                      </div>
                    </div>
                  );
                })}
              </div>
              {agents.length === 0 && (
                <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                  <Terminal className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 mb-2">No agents registered yet</p>
                  <p className="text-xs text-gray-400">Register an agent to start collecting security logs</p>
                </div>
              )}
            </div>
          )}


          {/* ── CVE FEED ──────────────────────────────────── */}
          {view === 'cve' && (
            <div className="space-y-4" data-testid="cve-feed">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Search CVEs</Label>
                    <Input value={cveSearch} onChange={e => setCveSearch(e.target.value)} placeholder="e.g., PowerShell, RDP, Apache..." className="mt-1 rounded-lg" data-testid="cve-search-input"
                      onKeyDown={e => e.key === 'Enter' && handleSearchCves()} />
                  </div>
                  <div>
                    <Label className="text-xs">Severity</Label>
                    <Select value={cveSeverity} onValueChange={v => setCveSeverity(v === 'all' ? '' : v)}>
                      <SelectTrigger className="mt-1 rounded-lg w-28" data-testid="cve-severity-filter"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleSearchCves} disabled={cveLoading} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="cve-search-btn">
                    <Search className="w-3.5 h-3.5 mr-1" />{cveLoading ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {cves.map(cve => (
                  <div key={cve.id} className={`bg-white border rounded-lg p-5 ${cve.severity === 'critical' ? 'border-red-200' : cve.severity === 'high' ? 'border-orange-200' : 'border-gray-200'}`} data-testid={`cve-${cve.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold font-mono text-[#D4A843]">{cve.id}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SEV_STYLES[cve.severity] || 'bg-gray-100 text-gray-600'}`}>{cve.severity}</span>
                          {cve.cvss_score > 0 && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cve.cvss_score >= 9 ? 'bg-red-600 text-white' : cve.cvss_score >= 7 ? 'bg-orange-500 text-white' : 'bg-amber-100 text-amber-700'}`}>CVSS {cve.cvss_score}</span>}
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{cve.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          {cve.published && <span className="text-[10px] text-gray-400">Published: {new Date(cve.published).toLocaleDateString()}</span>}
                          {cve.weaknesses?.length > 0 && cve.weaknesses.map((w, i) => (
                            <span key={`item-${i}`} className="text-[10px] font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{w}</span>
                          ))}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="text-xs rounded shrink-0 text-[#D4A843] border-[#D4A843]/30 hover:bg-[#D4A843]/5"
                        onClick={() => handleCreateRuleFromCve(cve)}
                        disabled={creatingCveRule === cve.id}
                        data-testid={`cve-rule-${cve.id}`}
                      >
                        <Zap className="w-3 h-3 mr-1" />{creatingCveRule === cve.id ? 'Creating...' : 'Create Rule'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {cves.length === 0 && !cveLoading && <p className="text-center text-gray-400 text-sm py-8">No CVEs found. Try searching or adjusting filters.</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
